import { useEffect, useMemo, useState } from 'react';
import {
  clearOverride,
  getEffectivePricing,
  hasOverride,
  setOverride,
  type Pricing,
} from '../pricingOverrides';
import type { ApiEndpointModel } from '../types';
import { TierSettingsCard } from './TierSettingsCard';

/** Pricing fields we know how to tune, in display order. */
const FIELD_META: { key: string; label: string; help: string; step: string }[] = [
  { key: 'per_call', label: 'Price per call', help: 'Flat cost charged per generation ($).', step: '0.001' },
  {
    key: 'input_per_m',
    label: 'Input tokens (per 1M)',
    help: 'Cost per 1M input tokens ($).',
    step: '0.01',
  },
  {
    key: 'output_per_m',
    label: 'Output tokens (per 1M)',
    help: 'Cost per 1M output tokens ($). Image output is billed here.',
    step: '0.01',
  },
];

interface SettingsTabProps {
  model: ApiEndpointModel;
}

export function SettingsTab({ model }: SettingsTabProps) {
  // Only expose the pricing fields this model actually declares.
  const activeFields = useMemo(
    () => FIELD_META.filter((f) => model.pricing[f.key] != null),
    [model.pricing],
  );

  const [values, setValues] = useState<Record<string, string>>({});
  const [overridden, setOverridden] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Load the effective (default + saved override) values whenever the model changes.
  useEffect(() => {
    const eff = getEffectivePricing(model);
    const next: Record<string, string> = {};
    for (const f of activeFields) {
      next[f.key] = eff[f.key] != null ? String(eff[f.key]) : '';
    }
    setValues(next);
    setOverridden(hasOverride(model.key));
  }, [model, activeFields]);

  function handleChange(key: string, value: string) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    const pricing: Pricing = {};
    for (const f of activeFields) {
      const raw = values[f.key];
      if (raw != null && raw.trim() !== '') {
        const num = Number(raw);
        if (!Number.isNaN(num)) pricing[f.key] = num;
      }
    }
    setOverride(model.key, pricing);
    setOverridden(true);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  }

  function handleReset() {
    clearOverride(model.key);
    const next: Record<string, string> = {};
    for (const f of activeFields) {
      next[f.key] = model.pricing[f.key] != null ? String(model.pricing[f.key]) : '';
    }
    setValues(next);
    setOverridden(false);
  }

  return (
    <div className="max-w-2xl space-y-5">
      {model.tiered && <TierSettingsCard model={model} />}

      <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2 className="text-sm font-semibold text-slate-700">Cost tuning</h2>
          {overridden && (
            <span className="text-[11px] font-medium text-warning-700 bg-warning-50 rounded-full px-2 py-0.5">
              Custom pricing active
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Tune the cost of <span className="font-medium text-slate-700">{model.label}</span> for this
          browser. These values change how cost is <em>displayed</em> in the Playground and Analytics;
          they do not change the price recorded on the server.
        </p>

        {activeFields.length === 0 ? (
          <p className="text-sm text-slate-400">This endpoint has no tunable pricing fields.</p>
        ) : (
          <div className="space-y-4">
            {activeFields.map((f) => (
              <label key={f.key} className="block">
                <span className="text-xs font-medium text-slate-500">{f.label}</span>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step={f.step}
                    value={values[f.key] ?? ''}
                    onChange={(e) => handleChange(f.key, e.target.value)}
                    placeholder={model.pricing[f.key] != null ? String(model.pricing[f.key]) : '0'}
                    className="w-full rounded-lg border border-slate-200 pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
                  />
                </div>
                <span className="mt-1 block text-[11px] text-slate-400">
                  {f.help} Default: {model.pricing[f.key]}.
                </span>
              </label>
            ))}

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600"
              >
                {savedFlash ? 'Saved' : 'Save pricing'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={!overridden}
                className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Reset to default
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
        <p className="font-medium text-slate-600 mb-1">How the displayed cost is computed</p>
        <p>
          For per-call endpoints the tuned price is applied exactly. For token-priced endpoints the
          server does not return token counts, so the tuned price is applied as a proportional
          estimate of the recorded cost (shown with an &ldquo;est.&rdquo; marker).
        </p>
      </div>
    </div>
  );
}
